import { useSearchParams } from 'next/navigation';

import { routes } from '~constants/routes';
import { useAuthActionsContext } from '~context/auth';
import {
	NsfwReason,
	RegisterInboundByPath,
	RegisterOutboundByPath,
} from '~features/annals';
import { useSwitchSafeModeWithAgeValidation } from '~features/nsfw';
import { useNavigationLinksAnalytics } from '~hooks/useNavigationLinks/useNavigationLinksAnalytics';
import { useSession } from '~hooks/useSession';
import { usePathname, useRouter } from '~i18n/navigation';

import { useNavigationPaths, useNavigationPathsSideMenu } from './paths';
import type {
	ExtendedNavigationElement,
	ExtendedNavigationPath,
	NavigationPath,
} from './types';

export const useNavigationLinks = (sideMenu = false) => {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const navigationPaths = useNavigationPaths();
	const navigationPathsSideMenu = useNavigationPathsSideMenu();

	const { isDemoUser, isSafeMode } = useSession();
	const { openRegister } = useAuthActionsContext();
	const router = useRouter();

	const updateSafeMode = useSwitchSafeModeWithAgeValidation();
	const { annalsNavigationClick } = useNavigationLinksAnalytics(sideMenu);

	const isActive = (
		targetPath: string,
		ignoreQueryParams: { [key: string]: string } = {}
	) => {
		if (targetPath === routes.explore.home) {
			return Object.values(routes.explore).includes(pathname);
		}

		const url = new URL(`https://example.com${targetPath}`);
		const targetPathname = url.pathname;
		const targetParams = url.searchParams;

		const pathMatches = pathname.startsWith(targetPathname);

		const queryMatches = Array.from(targetParams.keys()).every(
			(key) => searchParams.get(key) === targetParams.get(key)
		);

		const ignoreMatches = Object.entries(ignoreQueryParams).every(
			([key, value]) => searchParams.get(key) !== value
		);

		return pathMatches && queryMatches && ignoreMatches;
	};

	const links: ExtendedNavigationElement[] = [];
	const mapPath = (item: NavigationPath) => {
		const { path, isDemo, isSafe, withoutQuery } = item;

		const handle = (e: React.SyntheticEvent) => {
			annalsNavigationClick(path)();

			const cb = () => {
				return router.push(path, { scroll: false });
			};

			if (!isDemo && isDemoUser) {
				e.preventDefault();
				openRegister({
					cb,
					analytics: {
						inbound: RegisterInboundByPath[path],
						outbound: RegisterOutboundByPath[path],
					},
				});
			} else if (!isSafe) {
				e.preventDefault();
				if (isSafeMode) {
					void updateSafeMode(false, {
						force: true,
						nsfwReason: NsfwReason.NavigationLink,
						registerInbound: RegisterInboundByPath[path],
						registerOutbound: RegisterOutboundByPath[path],
					}).then(cb);
				} else {
					cb();
				}
			}
		};

		return {
			...item,
			active: isActive(path, withoutQuery),
			handle,
		};
	};
	(sideMenu ? navigationPathsSideMenu : navigationPaths).forEach((item) => {
		if ('children' in item) {
			const children: ExtendedNavigationPath[] = item.children.map((child) =>
				mapPath(child)
			);
			const active = children.some((child) => child.active);
			links.push({
				...item,
				children,
				active,
				handle: () => null,
			});
		} else {
			links.push(mapPath(item));
		}
	});

	return links;
};
